import { MAX_FILE_SIZE_MB } from "@config/constants";

export const SUPPORT_EMAIL = "support@meridian-ai.com";

export const SupportLink = () => (
  <a
    href={`mailto:${SUPPORT_EMAIL}`}
    target="_blank"
    rel="noreferrer"
    className="font-bold underline"
  >
    support
  </a>
);

export const CONTACT_SUPPORT = (
  <>
    Contact <SupportLink /> if you continue to have issues.
  </>
);

export const ERROR_STRINGS = {
  GENERIC: "Something went wrong. Please check your connection and try again.",
  ACTIVATE_PERSON: "We couldn't activate this person.",
  ADD_TAG: "We couldn't add the tag.",
  LOAD_AI_CONTEXT: "We couldn't load AI context.",
  UPDATE_AI_CONTEXT: "We couldn't update AI context.",
  CONNECTION_LOST: "Your connection was lost. Please check your internet and try again.",
  CREATE_COMPANY: "We couldn't create the company.",
  CREATE_DEAL: "We couldn't create the deal.",
  CREATE_MEETING_OBJECT: "We couldn't link the object to your meeting.",
  EDITOR_GET: "We couldn't retrieve the content.",
  EDITOR_INSERT: "We couldn't insert the content.",
  LOAD_EMAIL: "We couldn't load your email details.",
  LOGIN: "We couldn't log you in.",
  REMOVE_EMAIL: "We couldn't remove your email.",
  REMOVE_MEETING: "We couldn't remove your meeting.",
  REMOVE_TAG: "We couldn't remove the tag.",
  REMOVE_THREAD: "We couldn't remove your thread.",
  SAVE_NOTE: "We couldn't save your note.",
  SYNC_EMAIL: "We couldn't sync your email.",
  SYNC_MEETING: "We couldn't sync your meeting.",
  UNSUBSCRIBE: "We couldn't unsubscribe from this thread.",
  UPDATE_COMPANY: "We couldn't update the company.",
  UPDATE_MEETING: "We couldn't update your meeting.",
} as const;

export const ERROR_MESSAGES = {
  GENERIC: (
    <>
      {ERROR_STRINGS.GENERIC} {CONTACT_SUPPORT}
    </>
  ),
  ADD_TAG: (
    <>
      {ERROR_STRINGS.ADD_TAG} {CONTACT_SUPPORT}
    </>
  ),
  LOAD_AI_CONTEXT: (
    <>
      {ERROR_STRINGS.LOAD_AI_CONTEXT} {CONTACT_SUPPORT}
    </>
  ),
  CONNECTION_LOST: <>{ERROR_STRINGS.CONNECTION_LOST}</>,
  CREATE_COMPANY: (
    <>
      {ERROR_STRINGS.CREATE_COMPANY} {CONTACT_SUPPORT}
    </>
  ),
  CREATE_DEAL: (
    <>
      {ERROR_STRINGS.CREATE_DEAL} {CONTACT_SUPPORT}
    </>
  ),
  EDITOR_GET: (
    <>
      {ERROR_STRINGS.EDITOR_GET} {CONTACT_SUPPORT}
    </>
  ),
  EDITOR_INSERT: (
    <>
      {ERROR_STRINGS.EDITOR_INSERT} {CONTACT_SUPPORT}
    </>
  ),
  LOAD_EMAIL: (
    <>
      {ERROR_STRINGS.LOAD_EMAIL} {CONTACT_SUPPORT}
    </>
  ),
  LOGIN: (
    <>
      {ERROR_STRINGS.LOGIN} {CONTACT_SUPPORT}
    </>
  ),
  REMOVE_EMAIL: (
    <>
      {ERROR_STRINGS.REMOVE_EMAIL} {CONTACT_SUPPORT}
    </>
  ),
  REMOVE_MEETING: (
    <>
      {ERROR_STRINGS.REMOVE_MEETING} {CONTACT_SUPPORT}
    </>
  ),
  REMOVE_TAG: (
    <>
      {ERROR_STRINGS.REMOVE_TAG} {CONTACT_SUPPORT}
    </>
  ),
  REMOVE_THREAD: (
    <>
      {ERROR_STRINGS.REMOVE_THREAD} {CONTACT_SUPPORT}
    </>
  ),
  SAVE_NOTE: (
    <>
      {ERROR_STRINGS.SAVE_NOTE} {CONTACT_SUPPORT}
    </>
  ),
  SYNC_EMAIL: (
    <>
      {ERROR_STRINGS.SYNC_EMAIL} {CONTACT_SUPPORT}
    </>
  ),
  SYNC_MEETING: (
    <>
      {ERROR_STRINGS.SYNC_MEETING} {CONTACT_SUPPORT}
    </>
  ),
  UNSUBSCRIBE: (
    <>
      {ERROR_STRINGS.UNSUBSCRIBE} {CONTACT_SUPPORT}
    </>
  ),
  UPDATE_COMPANY: (
    <>
      {ERROR_STRINGS.UPDATE_COMPANY} {CONTACT_SUPPORT}
    </>
  ),
  UPDATE_MEETING: (
    <>
      {ERROR_STRINGS.UPDATE_MEETING} {CONTACT_SUPPORT}
    </>
  ),
} as const;

// Elements rather than markup strings: toasts render their message as a node, so tags in a plain
// string would show up literally. Pass these through ToastService.showUserError.
export const SUCCESS_MESSAGES = {
  COMPANY_UPDATED: (title: string) => `Company "${title}" updated`,
  DEAL_UPDATED: (
    <>
      Change applied to <b>Deal</b>
    </>
  ),
  SOURCE_COMPANY_UPDATED: (
    <>
      Change applied to <b>Company</b><br /> and <b>active Deal</b>
    </>
  ),
  SOURCE_COMPANY_WITH_MULTI_ACTIVE_DEALS_UPDATED: (
    <>
      Change applied to <b>Company</b><br /> and <b>primary active Deal</b>
    </>
  ),
};

export const UPLOAD_ERROR_MESSAGES = {
  ALREADY_EXIST: "File already exists.",
  FOLDERS: "Folders not supported for upload",
  IC_MEMO_CIM_DUPLICATE: "IC Memo and CIM can only be associated with one deal at a time.",
  IC_MEMO_CIM_FORMAT: "IC Memo, CIM must be PDF, DOCX, DOC, PPTX, or PPT",
  SINGLE_FILE_ONLY:
    "You can drag and drop or upload only one file for extraction. " +
    "Please leave only one file to continue.",
  SIZE_LIMIT_EXCEEDED: (
    <>
      The maximum file size is {MAX_FILE_SIZE_MB} MB. If you need to upload it, please contact{" "}
      <SupportLink />.
    </>
  ),
  UNSUPPORTED:
    "Cannot upload - unsupported file type. " + "Supported types: PDF, DOCX, XLSM, XLSX, PPTX, TXT",
};

export const AI_CONTEXT_MESSAGES = {
  UPDATING_ERROR: "Error Updating.",
};
